import React, { useEffect, useMemo, useState } from "react";
import API from "../../services/api";

const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const notifyStudioUsageUpdated = () => {
  const value = String(Date.now());
  localStorage.setItem(STUDIO_USAGE_REFRESH_KEY, value);
  window.dispatchEvent(new CustomEvent("hookora:studio-usage-updated"));
};

const SaveStudioAssetButton = ({
  toolType,
  title,
  topic,
  platform,
  content,
  meta = null
}) => {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const buttonColors = useMemo(
    () => ({
      defaultBg: isDark ? "#e2e8f0" : "#111827",
      defaultText: isDark ? "#020617" : "#ffffff",
      savedBg: "#16a34a",
      savedText: "#ffffff"
    }),
    [isDark]
  );

  const handleSave = async () => {
    if (!content || !String(content).trim()) return;

    try {
      setSaving(true);
      setSaved(false);

      await API.post("/studio-assets/", {
        tool_type: toolType,
        title,
        topic,
        platform,
        content,
        meta
      });

      setSaved(true);
      notifyStudioUsageUpdated();
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Failed to save studio asset:", err);
      alert(err?.response?.data?.detail || "Failed to save item to library.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      onClick={handleSave}
      disabled={saving}
      style={{
        padding: "10px 14px",
        borderRadius: "10px",
        border: "none",
        backgroundColor: saved ? buttonColors.savedBg : buttonColors.defaultBg,
        color: saved ? buttonColors.savedText : buttonColors.defaultText,
        cursor: saving ? "not-allowed" : "pointer",
        fontWeight: "600",
        opacity: saving ? 0.8 : 1
      }}
    >
      {saving ? "Saving..." : saved ? "Saved!" : "Save to Library"}
    </button>
  );
};

export default SaveStudioAssetButton;